import { useState, useRef } from 'react';
import XLight from '@/assets/xLight.svg';
import Modal from '../../general/Modal';

interface UploadDialogProps {
  show: boolean;
  onOk: (files: FileList) => void;
  onCancel: () => void;
}

export default function UploadDialog({ show, onOk, onCancel }: UploadDialogProps) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      const fileList = e.dataTransfer.files;
      onOk(fileList);
      setSelectedFiles([]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(files);
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOk = () => {
    if (selectedFiles.length > 0) {
      const fileList = new DataTransfer();
      selectedFiles.forEach((file) => fileList.items.add(file));
      onOk(fileList.files);
      setSelectedFiles([]);
    }
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    onCancel();
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <Modal
      open={show}
      onClose={handleCancel}
      disableCloseOnBackdropClick
      contanerClassName="w-[550px] max-w-[90vw] bg-white shadow-xl"
    >
      <div className="flex items-center justify-between p-4">
        <h3 className="text-lg font-medium text-gray-900">Select files to upload</h3>
      </div>

      <div className="p-6">
        <div
          className={`mb-4 flex h-[200px] w-full items-center justify-center rounded border-2 border-dashed transition-colors ${
            isDragOver ? 'border-gray-400 bg-gray-50' : 'border-gray-300'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="text-center text-gray-600">
            <p className="text-base">Drag and drop files here</p>
          </div>
        </div>

        <div
          className="flex w-full items-center gap-2 text-sm text-gray-600"
          onClick={() => fileInputRef.current?.click()}
        >
          <svg className="h-4 w-4 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
          </svg>
          <div className="w-full border-b border-solid border-black">
            <span className="cursor-pointer text-gray-600">or click here to choose files to upload</span>
            <input
              ref={fileInputRef}
              type="file"
              placeholder="or click here to choose files to upload"
              multiple
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
        </div>

        {selectedFiles.length > 0 ? (
          <div className="mt-4">
            <div className="max-h-32 space-y-2 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between rounded border p-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium">{file.name}</span>
                    <span className="text-gray-500">({formatFileSize(file.size)})</span>
                  </div>
                  <XLight
                    className="ml-2 h-4 w-4 cursor-pointer text-red-500 hover:text-red-700"
                    onClick={() => removeFile(index)}
                  />
                </div>
              ))}
            </div>
            <p className="mt-2 text-end text-xs text-gray-500">
              {selectedFiles.length} files ({formatFileSize(selectedFiles.reduce((sum, file) => sum + file.size, 0))} in
              total)
            </p>
          </div>
        ) : (
          <p className="mt-2 text-end text-xs text-gray-500">0 files (0 B in total)</p>
        )}
      </div>

      <div className="flex justify-end gap-3 bg-gray-50 p-4">
        <button
          onClick={handleOk}
          disabled={selectedFiles.length === 0}
          className={`h-10 min-w-[71px] rounded-3xl px-4 py-2 ${
            selectedFiles.length === 0
              ? 'cursor-not-allowed bg-[#f5f5f5] text-gray-500'
              : 'hover:bg-black-700 cursor-pointer bg-black text-white'
          }`}
        >
          Ok
        </button>
        <button
          onClick={handleCancel}
          className="hover:bg-gray-3 h-10 min-w-[90px] cursor-pointer rounded-3xl bg-[#f5f5f5] px-4 py-2 text-gray-700 transition-all duration-300 ease-in-out hover:scale-105 hover:text-gray-900 hover:shadow-md"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
}
